import "./globals.js"; // needs to be the first import
import App from "./App.js";
import Backbone from "backbone";
import Ctrl from "ctrljs";
import checkIfVersionSupported from "./checkIfVersionSupported.js";
import config from "./config.js";
import Fetch from "./Fetch.js";
import loadingSpinner from "./loadingSpinner.js";
import router from "./router.js";
import Util from "./Util.js";
import $ from "jquery";
import Hammer from "hammerjs";

(async function () {
  let loadFailedEl = document.getElementById("loadFailed");
  try {
    await updateBaseUrl();
    window.App = App; // for debugging
    await domReady();
    await importCommonStyleSheets();
    await checkIfVersionSupported();
    removeInitialLoadingElements();
    addRootLayoutElements();
    App.init();
    await App.loadAppFacility();
    let logoIcon = document.getElementById("nb-logoIcon");
    iosSpecificStyling(logoIcon);
    globalDefs();
    await router.defineRoutes(null);
    navigateHomeWhenLogoIconClicked(logoIcon);
    loadingSpinner.observeCtrlLoads();
    Ctrl.observe(document.body);
    startBackboneHistory();
  } catch (e) {
    document.body.innerHTML = "";
    document.body.appendChild(loadFailedEl);
    loadFailedEl.style.display = "block";
    loadFailedEl.style.marginTop = -1 * (loadFailedEl.offsetHeight / 2) + "px";
    console.error(e);
  }
})();

async function updateBaseUrl() {
  // If Cordova, get latest version from the server so we can load the latest
  // code without needing to force an app upgrade every time.
  if (window.cordova) {
    let latestClientVersion = parseInt(
      await Fetch.fetch(`${config.serverUrl}/latestClientVersion`),
    );
    config.baseUrl = `https://${config.cdnHostname}/app/v${latestClientVersion}`;
  }
}

async function domReady() {
  // Wait till DOM and/or Cordova is ready
  await new Promise((resolve) => {
    function onDOMReady() {
      if (window.cordova) {
        document.addEventListener("deviceready", resolve, false);
      } else {
        resolve();
      }
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", onDOMReady);
    } else {
      onDOMReady();
    }
  });
}

async function importCommonStyleSheets() {
  // Import library style sheets
  let libStylesheets = [
    `${config.baseUrl}/lib/bootstrap-css/bootstrap.css`,
    `${config.baseUrl}/lib/bootstrap-datepicker3.css`,
    `${config.baseUrl}/lib/bootstrap-timepicker.css`,
    `${config.baseUrl}/lib/cropper.css`,
    `${config.baseUrl}/lib/glyphicons.css`,
    `${config.baseUrl}/lib/vex.css`,
    `${config.baseUrl}/lib/vex-theme-os.css`,
  ];
  await Util.importCSS(libStylesheets);

  // Import app style sheets (after library style sheets so cascading can
  // work properly).
  let appStylesheets = [
    `${config.baseUrl}/index.css`,
    `${config.baseUrl}/theme.css`,
  ];
  if (window.cordova) {
    // Cordova stylesheets
    appStylesheets.push(`${config.baseUrl}/index-cordova.css`);
  } else {
    // Import custom scroll bar stylesheet only for web
    let isChrome = window.chrome;
    let isSafari = window.navigator.userAgent.indexOf("AppleWebKit") > -1;
    if (!window.cordova && (isChrome || isSafari)) {
      appStylesheets.push(`${config.baseUrl}/customScrollBar.css`);
    }
  }
  await Util.importCSS(appStylesheets);
}

function removeInitialLoadingElements() {
  // Remove initial loading elements
  let initLoadingSpinner = document.getElementById("initLoadingSpinner");
  if (initLoadingSpinner) {
    document.body.removeChild(initLoadingSpinner);
  }
  document.body.removeChild(document.getElementById("loadFailed"));
}

function addRootLayoutElements() {
  // Add header and main elements. These are not put directly in
  // index.html to allow a graceful loading screen.
  document.body.innerHTML = `
            <header id='header'>
                <div id='navbar'>
                    <span id='nb-backIcon' class='glyphicon glyphicon-chevron-left'></span>
                    <img id='nb-logoIcon' style="display: none"/>
                    <div id='nb-actions'></div>
                </div>
            </header>
            <main id='main'></main>
            <div id='videoCallContainer'></div>`;
  document.getElementById("header").style.display = "block";
}

function iosSpecificStyling(logoIcon) {
  // iOS-specific styling
  if (window.device && window.device.platform === "iOS") {
    // Show back button on iOS
    let backIcon = document.getElementById("nb-backIcon");
    backIcon.style.display = "inline-block";
    backIcon.addEventListener("click", () => history.back());

    // Add custom class to logo to iOS
    logoIcon.classList.add("nb-logoIcon-ios");
  }
}

function navigateHomeWhenLogoIconClicked(logoIcon) {
  // Go home when icon clicked
  logoIcon.addEventListener("click", () => {
    if (!App.isLogoLinkEnabled()) {
      return;
    }
    router.navigate("", { trigger: true });
  });
}

function startBackboneHistory() {
  // Use pushState everywhere except iOS
  const usePushState =
    !window.cordova || !window.device || window.device.platform !== "iOS";

  // Start Backbone history
  let routeFound = Backbone.history.start({
    pushState: usePushState,
    root: config.backboneRoot,
  });

  if (!routeFound) {
    // The route was not found. Either it is an invalid route, or it
    // will be defined after the user is signed in according to their
    // role. So for now we proxy to the sign in route.
    const fragment = Backbone.history.getFragment();
    router.navigate(`sign-in?next=${fragment}`, { trigger: true });

    // HACK: Sometimes the router.navigate() call above doesn't work. There's
    // no obvious reason it shouldn't. To fix it, we check after some
    // time and try again repeatedly if it didn't.
    let intervalCount = 0;
    let interval = setInterval(() => {
      ++intervalCount;
      if (intervalCount > 1000) {
        clearInterval(interval);
        console.error("Could not navigate to starting route.");
      }
      if (Backbone.history.getFragment() === fragment) {
        router.navigate("", { trigger: true });
      } else {
        clearInterval(interval);
      }
    }, 10);
  }
}

function globalDefs() {
  // Global jQuery needed by Bootstrap
  window.$ = window.jQuery = $;

  // Global Hammer needed by jquery-hammerjs
  window.Hammer = Hammer;
}

export default { addRootLayoutElements, globalDefs };
